import { List, Show, useGetList, useNavigate } from 'react-admin';
import { useState, useMemo } from 'react';
import { Box, Typography, Paper, Chip, Grid, Avatar, IconButton, Button, TextField, Select, MenuItem as MuiMenuItem, FormControl, InputLabel, Collapse, ListItemButton, ListItemIcon, ListItemText, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Folder, Description, PictureAsPdf, InsertDriveFile, Visibility, Delete, Download, CheckCircle, Warning, Close, ExpandMore, ChevronRight, Business, Category } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import type { FileRecord } from '@/types/entities';
import type { FileNamespaceRecord } from '@/types/file';

const SUBSYSTEMS = ['HR', 'Finance', 'Compliance', 'OHS', 'SkillsDevelopment', 'Onboarding', 'IndustrialRelations', 'Offboarding', 'EmploymentEquity'] as const;
const DOCUMENT_TYPES = ['UI19', 'CertificateOfService', 'EmploymentContract', 'PersonalDocuments', 'DisciplinaryNotice', 'DisciplinaryOutcome', 'PipPlan', 'WSPReport', 'ATRReport', 'EEA2Form', 'EEA4Form', 'FirstAidCertificate', 'SafetyTrainingCertificate', 'MedicalCertificate', 'InjuryReport', 'Other'] as const;
const CATEGORIES = ['StatutoryDocument', 'EmploymentContract', 'PersonalDocument', 'HRProcessDocument', 'TrainingDocument', 'FinanceDocument', 'Report', 'Attachment'] as const;
const STATUSES = ['Active', 'Archived', 'Deleted'] as const;

const SUBSYSTEM_COLORS: Record<string, string> = {
  Onboarding: '#1565c0',
  Offboarding: '#4a148c',
  IndustrialRelations: '#1a237e',
  SkillsDevelopment: '#00695c',
  EmploymentEquity: '#6a1b9a',
  OHS: '#1b5e20',
  Finance: '#e65100',
  Compliance: '#37474f',
  HR: '#455a64',
};

const getFileIcon = (mimeType: string) => {
  if (mimeType === 'application/pdf') return <PictureAsPdf />;
  if (mimeType.startsWith('image/')) return <Description />;
  return <InsertDriveFile />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface NamespaceNode {
  id: string;
  label: string;
  type: 'root' | 'subsystem' | 'year' | 'month' | 'employee' | 'documentType' | 'workflow';
  count: number;
  children: NamespaceNode[];
  files: FileNamespaceRecord[];
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const buildFileUrn = (file: FileNamespaceRecord): string => {
  const parts = [`urn:rosta:${(file.subsystem ?? '').toLowerCase()}`];
  if (file.year) parts.push(String(file.year));
  if (file.month) parts.push(MONTH_NAMES[file.month - 1]);
  if (file.workflow?.workflowTitle) parts.push(file.workflow.workflowTitle);
  if (file.employeeName) parts.push(file.employeeName);
  if (file.documentType) parts.push(file.documentType);
  if (file.workflow?.workflowInstanceId) parts.push(file.workflow.workflowInstanceId);
  parts.push(file.id);
  return parts.join(':');
};

const buildNamespaceTree = (files: FileNamespaceRecord[]): NamespaceNode[] => {
  const root: NamespaceNode = { id: 'root', label: 'urn:rosta', type: 'root', count: files.length, children: [], files: [] };

  const getOrCreateChild = (parent: NamespaceNode, id: string, label: string, type: NamespaceNode['type']): NamespaceNode => {
    let child = parent.children.find(c => c.id === id);
    if (!child) {
      child = { id, label, type, count: 0, children: [], files: [] };
      parent.children.push(child);
    }
    return child;
  };

  files.forEach(file => {
    if (!file.documentType) return;
    const subsystemNode = getOrCreateChild(root, `subsystem-${file.subsystem}`, file.subsystem ?? '', 'subsystem');
    const yearNode = getOrCreateChild(subsystemNode, `subsystem-${file.subsystem}-year-${file.year}`, String(file.year ?? ''), 'year');
    const monthNode = file.month
      ? getOrCreateChild(yearNode, `subsystem-${file.subsystem}-year-${file.year}-month-${file.month}`, MONTH_NAMES[file.month - 1], 'month')
      : yearNode;
    const docTypeNode = getOrCreateChild(monthNode, `subsystem-${file.subsystem}-year-${file.year}-month-${file.month ?? 0}-doctype-${file.documentType}`, file.documentType, 'documentType');

    let parentNode: NamespaceNode;
    if (file.workflow?.workflowTitle) {
      const workflowNode = getOrCreateChild(docTypeNode, `subsystem-${file.subsystem}-year-${file.year}-month-${file.month ?? 0}-doctype-${file.documentType}-workflow-${file.workflow.workflowTitle}`, file.workflow.workflowTitle, 'workflow');
      parentNode = file.employeeName
        ? getOrCreateChild(workflowNode, `subsystem-${file.subsystem}-year-${file.year}-month-${file.month ?? 0}-doctype-${file.documentType}-workflow-${file.workflow.workflowTitle}-employee-${file.employeeName}`, file.employeeName, 'employee')
        : workflowNode;
    } else if (file.employeeName) {
      parentNode = getOrCreateChild(docTypeNode, `subsystem-${file.subsystem}-year-${file.year}-month-${file.month ?? 0}-doctype-${file.documentType}-employee-${file.employeeName}`, file.employeeName, 'employee');
    } else {
      parentNode = docTypeNode;
    }
    parentNode.files.push(file);
  });

  const computeCounts = (node: NamespaceNode): number => {
    const childFileCount = node.children.reduce((sum, child) => sum + computeCounts(child), 0);
    node.count = node.files.length + childFileCount;
    return node.count;
  };

  const sortChildren = (node: NamespaceNode) => {
    node.children.sort((a, b) => {
      if (a.type === 'year' && b.type === 'year') return parseInt(a.label) - parseInt(b.label);
      if (a.type === 'month' && b.type === 'month') {
        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthOrder.indexOf(a.label) - monthOrder.indexOf(b.label);
      }
      return a.label.localeCompare(b.label);
    });
    node.children.forEach(sortChildren);
  };

  computeCounts(root);
  sortChildren(root);
  return root.children;
};

interface TreeNodeProps {
  node: NamespaceNode;
  selectedNodeId: string | null;
  onNodeSelect: (node: NamespaceNode) => void;
  expandedNodes: Set<string>;
  onToggle: (nodeId: string) => void;
  depth: number;
}

const NamespaceTreeNode = ({ node, selectedNodeId, onNodeSelect, expandedNodes, onToggle, depth }: TreeNodeProps) => {
  const isExpanded = expandedNodes.has(node.id);
  const isSelected = selectedNodeId === node.id;

  const getNodeIcon = () => {
    switch (node.type) {
      case 'root':
        return <Folder sx={{ fontSize: 18 }} />;
      case 'subsystem':
        return <Business sx={{ fontSize: 18 }} />;
      case 'year':
        return <Description sx={{ fontSize: 18 }} />;
      case 'month':
        return <Description sx={{ fontSize: 18 }} />;
      case 'employee':
        return <Avatar sx={{ fontSize: 16, width: 20, height: 20 }} />;
      case 'documentType':
        return <Category sx={{ fontSize: 18 }} />;
      case 'workflow':
        return <Description sx={{ fontSize: 18 }} />;
      default:
        return <InsertDriveFile sx={{ fontSize: 18 }} />;
    }
  };

  const getNodeColor = () => {
    if (node.type === 'subsystem') {
      return SUBSYSTEM_COLORS[node.label] || '#455a64';
    }
    return 'text.primary';
  };

  const handleClick = (e: React.MouseEvent) => {
    if (node.children.length > 0) {
      onToggle(node.id);
    }
    onNodeSelect(node);
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(node.id);
  };

  return (
    <Box>
      <ListItemButton
        onClick={handleClick}
        sx={{
          pl: 2 + depth * 2,
          py: 0.5,
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          mr: 0.5,
        }}
      >
        <ListItemIcon sx={{ minWidth: 28 }} onClick={handleToggleClick}>
          {node.children.length > 0 ? (isExpanded ? <ExpandMore sx={{ fontSize: 18 }} /> : <ChevronRight sx={{ fontSize: 18 }} />) : <Box sx={{ width: 18 }} />}
        </ListItemIcon>
        <ListItemIcon sx={{ minWidth: 28, color: getNodeColor() }}>
          {getNodeIcon()}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: node.type === 'subsystem' || node.type === 'root' ? 600 : 400 }}>
                {node.label}
              </Typography>
            </Box>
          }
        />
        <Chip label={node.count} size="small" sx={{ height: 20, fontSize: 11 }} />
      </ListItemButton>

      {(
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {node.children.map(child => (
            <NamespaceTreeNode
              node={child}
              selectedNodeId={selectedNodeId}
              onNodeSelect={onNodeSelect}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

interface FileListProps {
  files: FileNamespaceRecord[];
  isLoading: boolean;
}

const FileListContent = ({ files, isLoading }: FileListProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<FileNamespaceRecord | null>(null);
  const [selectedNode, setSelectedNode] = useState<NamespaceNode | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const filteredFiles = useMemo(() => {
    let result = files;
    if (statusFilter) {
      result = result.filter(f => f.status === statusFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(f =>
        f.fileName?.toLowerCase().includes(q) ||
        f.originalFileName?.toLowerCase().includes(q) ||
        f.documentType?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [files, statusFilter, searchQuery]);

  const tree = useMemo(() => buildNamespaceTree(filteredFiles), [filteredFiles]);

  const stats = {
    total: files.length,
    statutory: files.filter(f => f.category === 'StatutoryDocument').length,
    pending: files.filter(f => f.requirement?.required && !f.requirement?.fulfilled).length,
    active: files.filter(f => f.status === 'Active').length,
  };

  const totalSize = files.reduce((acc, f) => acc + (f.size || 0), 0);
  const formatSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const pieData = SUBSYSTEMS.map(s => ({
    label: s,
    value: files.filter(f => f.subsystem === s).length,
  })).filter(d => d.value > 0);

  const handleToggle = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleNodeSelect = (node: NamespaceNode) => {
    setSelectedNode(node);
    setSelectedFile(null);
  };

  const nodeFiles = selectedNode ? selectedNode.files : [];

  const handleFileSelect = (file: FileNamespaceRecord) => {
    setSelectedFile(file);
    setSelectedNode(null);
  };

  const handleViewFile = () => {
    if (selectedFile) {
      navigate(`/files/${selectedFile.id}/show`);
    }
  };

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="File Management"
          metrics={[
            { label: 'Total', value: String(stats.total), color: 'primary.main' },
            { label: 'Statutory', value: String(stats.statutory), color: 'info.main' },
            { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
            { label: 'Storage', value: formatSize(totalSize), color: 'text.secondary' },
          ]}
        />
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            label="Search"
            placeholder="File name..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
            fullWidth
            size="small"
          />
        </Paper>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filteredFiles.slice(0, 50).map((file: FileNamespaceRecord) => (
            <Paper key={file.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Avatar sx={{ bgcolor: SUBSYSTEM_COLORS[file.subsystem ?? ''] || '#455a64', width: 40, height: 40 }}>
                  {getFileIcon(file.mimeType)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" noWrap>{file.fileName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {file.subsystem} · {formatFileSize(file.size ?? 0)} · {file.status}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label={file.documentType} size="small" sx={{ bgcolor: SUBSYSTEM_COLORS[file.subsystem ?? ''], color: 'white' }} />
                {file.requirement?.required && !file.requirement?.fulfilled && (
                  <Chip icon={<Warning />} label="Required" size="small" color="error" />
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <ListPageHeader
        title="File Management"
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Statutory', value: String(stats.statutory), color: 'info.main' },
          { label: 'Pending Req.', value: String(stats.pending), color: 'warning.main' },
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Storage', value: formatSize(totalSize), color: 'text.secondary' },
        ]}
        pieChartData={pieData}
        pieChartTitle="By Subsystem"
      />

      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Search"
            placeholder="File name, type..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
            sx={{ flex: 1 }}
            size="small"
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as string)}
              size="small"
            >
              <MuiMenuItem value="">All Status</MuiMenuItem>
              {STATUSES.map(s => <MuiMenuItem key={s} value={s}>{s}</MuiMenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, overflow: 'hidden' }}>
        <Paper sx={{ width: 320, overflow: 'auto', flexShrink: 0 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Namespace Browser
            </Typography>
            <Typography variant="caption" color="text.secondary">
              urn:rosta:&lt;subsystem&gt;:&lt;category&gt;:&lt;file&gt;
            </Typography>
          </Box>
          <Box sx={{ p: 1, overflow: 'auto', flex: 1 }}>
            {tree.map(node => (
              <NamespaceTreeNode
                node={node}
                selectedNodeId={selectedNode?.id || null}
                onNodeSelect={handleNodeSelect}
                expandedNodes={expandedNodes}
                onToggle={handleToggle}
                depth={0}
              />
            ))}
          </Box>
        </Paper>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 2 }}>
          {selectedNode && (
            <Paper sx={{ overflow: 'auto', flexShrink: 0, maxHeight: 300 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedNode.label}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {selectedNode.type} · {nodeFiles.length} files
                </Typography>
              </Box>
              <Chip label={`${selectedNode.count} items`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }} />
            </Box>
            <Box sx={{ overflow: 'auto' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 80px', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                  <Typography variant="caption" fontWeight={600}>File Name</Typography>
                  <Typography variant="caption" fontWeight={600}>Size</Typography>
                  <Typography variant="caption" fontWeight={600}>Status</Typography>
                  <Typography variant="caption" fontWeight={600}>Sensitivity</Typography>
                  <Typography variant="caption" fontWeight={600}>Action</Typography>
                </Box>
                {nodeFiles.map((file: FileNamespaceRecord) => (
                  <Box
                    key={file.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 80px',
                      gap: 1,
                      p: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' },
                    }}
                    onClick={() => handleFileSelect(file)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getFileIcon(file.mimeType)}
                      <Typography variant="body2" noWrap title={file.fileName}>{file.fileName}</Typography>
                    </Box>
                    <Typography variant="body2">{formatFileSize(file.size ?? 0)}</Typography>
                    <Chip
                      label={file.status}
                      size="small"
                      color={file.status === 'Active' ? 'success' : file.status === 'Deleted' ? 'error' : 'default'}
                      sx={{ alignSelf: 'center', width: 'fit-content' }}
                    />
                    <Chip
                      label={file.legislation?.sensitivityLevel || '—'}
                      size="small"
                      color={
                        file.legislation?.sensitivityLevel === 'Restricted' ? 'error' :
                        file.legislation?.sensitivityLevel === 'Confidential' ? 'warning' :
                        file.legislation?.sensitivityLevel === 'Internal' ? 'info' : 'default'
                      }
                      sx={{ alignSelf: 'center', width: 'fit-content' }}
                    />
                    <Button variant="text" size="small" onClick={(e) => { e.stopPropagation(); handleFileSelect(file); }}>Select</Button>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
          )}

          <Paper sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            {selectedFile ? (
            <>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>{selectedFile.fileName}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip label={selectedFile.subsystem} size="small" sx={{ bgcolor: SUBSYSTEM_COLORS[selectedFile.subsystem ?? ''], color: 'white' }} />
                    <Chip label={selectedFile.category} size="small" variant="outlined" />
                    <Chip label={selectedFile.documentType} size="small" variant="outlined" />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" size="small" startIcon={<Visibility />} onClick={handleViewFile}>
                    View Details
                  </Button>
                </Box>
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Original File Name</Typography>
                      <Typography variant="body2">{selectedFile.originalFileName}</Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Size</Typography>
                      <Typography variant="body2" fontWeight={500}>{formatFileSize(selectedFile.size ?? 0)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Chip
                        label={selectedFile.status}
                        size="small"
                        color={selectedFile.status === 'Active' ? 'success' : selectedFile.status === 'Deleted' ? 'error' : 'default'}
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Storage Location</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: 12 }}>
                        {selectedFile.storage?.bucket}/{selectedFile.storage?.key}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Namespace URN</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 10, wordBreak: 'break-all' }}>
                        {buildFileUrn(selectedFile)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Tags</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {(selectedFile.tags || []).map((tag: string) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Applicable Acts</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {(selectedFile.legislation?.applicableActs || []).map((act: string) => (
                          <Chip key={act} label={act} size="small" sx={{ bgcolor: 'primary.light' }} />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Sections</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {(selectedFile.legislation?.sections || []).length > 0 ? (
                          (selectedFile.legislation?.sections ?? []).map((section: string) => (
                            <Chip key={section} label={section} size="small" variant="outlined" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Sensitivity</Typography>
                      <Chip
                        label={selectedFile.legislation?.sensitivityLevel || '—'}
                        size="small"
                        color={
                          selectedFile.legislation?.sensitivityLevel === 'Restricted' ? 'error' :
                          selectedFile.legislation?.sensitivityLevel === 'Confidential' ? 'warning' :
                          selectedFile.legislation?.sensitivityLevel === 'Internal' ? 'info' : 'default'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  </Grid>
                  {selectedFile.requirement?.required && (
                    <Grid size={{ xs: 12 }}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: selectedFile.requirement?.fulfilled ? 'success.50' : 'error.50' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {selectedFile.requirement?.fulfilled ? <CheckCircle color="success" /> : <Warning color="error" />}
                          <Typography variant="body2">
                            {selectedFile.requirement?.fulfilled ? 'Requirement Fulfilled' : 'Requirement Pending'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Required by: {selectedFile.requirement?.requiredByWorkflowStep || 'N/A'}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {selectedFile.workflow && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">Workflow</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" fontWeight={500}>{selectedFile.workflow.workflowTitle || selectedFile.workflow.workflowType}</Typography>
                          <Typography variant="caption" color="text.secondary">Type: {selectedFile.workflow.workflowType}</Typography>
                          {selectedFile.workflow.workflowInstanceId && (
                            <Typography variant="caption" display="block" color="text.secondary">Instance ID: {selectedFile.workflow.workflowInstanceId}</Typography>
                          )}
                          {selectedFile.workflow.sourceStep && (
                            <Typography variant="caption" display="block" color="text.secondary">Source Step: {selectedFile.workflow.sourceStep}</Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {selectedFile.workflow.producedByStep && (
                              <Chip label="Produced by step" size="small" color="success" />
                            )}
                            {selectedFile.workflow.producedAt && (
                              <Chip label={`Produced ${new Date(selectedFile.workflow.producedAt).toLocaleDateString('en-GB')}`} size="small" variant="outlined" />
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                  {selectedFile.relatedEntity && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">Related Employee</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" fontWeight={500}>{selectedFile.relatedEntity.name || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">Employee ID: {selectedFile.relatedEntity.id}</Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
                <Folder sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">Select a file from the namespace browser or table</Typography>
                <Typography variant="body2" color="text.disabled">
                  Click a node in the namespace browser to see files below
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

const FileList = () => {
  const { data: files, isLoading } = useGetList('files', { pagination: { page: 1, perPage: 1000 } });

  return (
    <FileListContent files={(files || []) as FileNamespaceRecord[]} isLoading={isLoading} />
  );
};

const FileShow = () => {
  const navigate = useNavigate();

  return (
    <Show render={({ record }) => {
      if (!record) return null;
      const r = record as FileNamespaceRecord;

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/files')} size="small" sx={{ color: 'text.secondary' }}>
              <Close />
            </IconButton>
          </Box>

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #37474f, #546e7a)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 24 }}>
                {getFileIcon(r.mimeType)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{r.fileName}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{r.originalFileName}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={r.documentType} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.9)', fontWeight: 600 }} />
                  <Chip label={r.subsystem} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Chip label={r.status} size="small" sx={{ bgcolor: r.status === 'Active' ? 'success.main' : 'error.main' }} />
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Size</Typography>
                <Typography variant="h6" fontWeight={700}>{formatFileSize(r.size ?? 0)}</Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">Namespace Path</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              urn:rosta:{(r.subsystem ?? '').toLowerCase()}:{r.category?.toLowerCase()}:{r.id.split(':')[0]}:{r.id.split(':')[1]}
            </Typography>
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>File Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box><Typography variant="caption" color="text.secondary">MIME Type</Typography><Typography variant="body2">{r.mimeType}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Category</Typography><Typography variant="body2">{r.category}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Tenant</Typography><Typography variant="body2">{r.id.split(':')[0]}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Created</Typography><Typography variant="body2">{r.metadata?.createdAt ? new Date(r.metadata.createdAt).toLocaleString('en-GB') : '—'}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Expires</Typography><Typography variant="body2">{r.expiresAt ? new Date(r.expiresAt).toLocaleDateString('en-GB') : 'Not set'}</Typography></Box>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Requirement Status</Typography>
                {r.requirement?.required ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {r.requirement?.fulfilled ? <CheckCircle color="success" /> : <Warning color="error" />}
                      <Typography variant="body2">
                        {r.requirement?.fulfilled ? `Fulfilled (${r.requirement?.fulfilledAt ? new Date(r.requirement.fulfilledAt).toLocaleDateString('en-GB') : ''})` : 'Not Fulfilled'}
                      </Typography>
                    </Box>
                    <Box><Typography variant="caption" color="text.secondary">Required by Step</Typography><Typography variant="body2">{r.requirement?.requiredByWorkflowStep || '—'}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">Verified By</Typography><Typography variant="body2">{r.requirement?.verifiedBy || '—'}</Typography></Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">Not required by any workflow</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {r.workflow && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Workflow Integration</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Workflow Type</Typography>
                  <Typography variant="body2" fontWeight={500}>{r.workflow.workflowType}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Instance ID</Typography>
                  <Typography variant="body2" fontWeight={500}>{r.workflow.workflowInstanceId || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Source Step</Typography>
                  <Typography variant="body2">{r.workflow.sourceStep || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Produced At</Typography>
                  <Typography variant="body2">{r.workflow.producedAt ? new Date(r.workflow.producedAt).toLocaleString('en-GB') : '—'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Related Entity</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography variant="body2" fontWeight={500}>{r.relatedEntity?.type || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">ID</Typography>
                <Typography variant="body2">{r.relatedEntity?.id || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body2" fontWeight={500}>{r.relatedEntity?.name || '—'}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Storage Details</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Method</Typography>
                <Typography variant="body2" fontWeight={500}>{r.storage?.method || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Bucket</Typography>
                <Typography variant="body2">{r.storage?.bucket || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">Key</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: 12 }}>{r.storage?.key || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">Hash (SHA256)</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: 12 }}>{r.hash?.value || '—'}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {r.legislation && ((r.legislation.applicableActs && r.legislation.applicableActs.length > 0) || r.legislation.sensitivityLevel) && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Legislation & Compliance</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Applicable Acts</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {(r.legislation?.applicableActs || []).map((act: string) => (
                      <Chip key={act} label={act} size="small" sx={{ bgcolor: 'primary.light' }} />
                    ))}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Sections</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {(r.legislation?.sections ?? []).length > 0 ? (
                      (r.legislation!.sections ?? []).map((section: string) => (
                        <Chip key={section} label={section} size="small" variant="outlined" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Sensitivity Level</Typography>
                  <Chip
                    label={r.legislation?.sensitivityLevel || '—'}
                    size="small"
                    color={
                      r.legislation?.sensitivityLevel === 'Restricted' ? 'error' :
                      r.legislation?.sensitivityLevel === 'Confidential' ? 'warning' :
                      r.legislation?.sensitivityLevel === 'Internal' ? 'info' : 'default'
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<Visibility />} disabled>View</Button>
            <Button variant="outlined" startIcon={<Download />} disabled>Download</Button>
            <Button variant="outlined" color="error" startIcon={<Delete />} disabled>Delete</Button>
          </Box>
        </Box>
      );
    }} />
  );
};

const FileEdit = () => (
  <Box sx={{ p: 3 }}>
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Edit File</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="File Name" fullWidth size="small" />
        <FormControl fullWidth size="small">
          <InputLabel>Subsystem</InputLabel>
          <Select label="Subsystem">
            {SUBSYSTEMS.map(s => <MuiMenuItem key={s} value={s}>{s}</MuiMenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Document Type</InputLabel>
          <Select label="Document Type">
            {DOCUMENT_TYPES.map(d => <MuiMenuItem key={d} value={d}>{d}</MuiMenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select label="Status">
            {STATUSES.map(s => <MuiMenuItem key={s} value={s}>{s}</MuiMenuItem>)}
          </Select>
        </FormControl>
      </Box>
    </Paper>
  </Box>
);

const FileCreate = () => (
  <Box sx={{ p: 3 }}>
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Create File</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="File Name" fullWidth size="small" />
        <TextField label="Original File Name" fullWidth size="small" />
        <FormControl fullWidth size="small">
          <InputLabel>Subsystem</InputLabel>
          <Select label="Subsystem">
            {SUBSYSTEMS.map(s => <MuiMenuItem key={s} value={s}>{s}</MuiMenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Category</InputLabel>
          <Select label="Category">
            {CATEGORIES.map(c => <MuiMenuItem key={c} value={c}>{c}</MuiMenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Document Type</InputLabel>
          <Select label="Document Type">
            {DOCUMENT_TYPES.map(d => <MuiMenuItem key={d} value={d}>{d}</MuiMenuItem>)}
          </Select>
        </FormControl>
        <TextField label="MIME Type" fullWidth size="small" defaultValue="application/pdf" />
      </Box>
    </Paper>
  </Box>
);

export const FileListShow = FileList;
export const FileShowPage = FileShow;
export const FileEditPage = FileEdit;
export const FileCreatePage = FileCreate;